import type {
	CountryResponse,
	ErpRevenueResponse,
	ErpStatsResponse,
	PackMonthDataResponse,
	PriceChangeResponse,
	VirtualPackErpResponse,
} from 'com.cogvio.pricemonitor.api';
import { formatDateFromString } from '~/libs/utils/format';

export const parseApiResult = (erpApiResult: VirtualPackErpResponse) => {
	const stats: ErpStatsResponse = erpApiResult?.stats || { quadrants: [], cumulativeRevenues: [] };
	const cumulativeStats = stats.cumulativeRevenues.reduce(
		(aggregator: { [countryCode: string]: ErpRevenueResponse }, stat) => ({
			...aggregator,
			[stat.country.alpha2]: stat,
		}),
		{}
	);

	const byMonth: Record<string, PackMonthDataResponse[]> =
		erpApiResult?.monthData.reduce(
			(aggregator: { [date: string]: PackMonthDataResponse[] }, data) => ({
				...aggregator,
				[data.priceDate]: (aggregator[data.priceDate] || []).concat([data]),
			}),
			{}
		) || {};

	const byCountry =
		erpApiResult?.monthData.reduce(
			(aggregator: { [countryCode: string]: PackMonthDataResponse['country'] & PackMonthDataResponse }, data) => ({
				...aggregator,
				[data.country.alpha2]: {
					...data,
					...data.country,
				},
			}),
			{}
		) || {};

	const allDataByCountry =
		erpApiResult?.monthData.reduce(
			(aggregator: { [countryCode: string]: PackMonthDataResponse[] }, data) => ({
				...aggregator,
				[data.country.alpha2]: (aggregator[data.country.alpha2] || []).concat([data]),
			}),
			{}
		) || {};

	const erpImpactedCountries =
		erpApiResult?.erpImpacted.reduce(
			(aggregator: { [countryCode: string]: CountryResponse }, data) => ({
				...aggregator,
				[data.name]: data,
			}),
			{}
		) || {};

	const sortedDates = Object.keys(byMonth).sort((aDate, bDate) =>
		new Date(aDate).getTime() < new Date(bDate).getTime() ? -1 : 1
	);

	const priceChangesByMonth =
		erpApiResult?.priceChanges.reduce(
			(aggregator: { [date: string]: PriceChangeResponse[] }, data) => ({
				...aggregator,
				[formatDateFromString(data.date)]: (aggregator[formatDateFromString(data.date)] || []).concat([data]),
			}),
			{}
		) || {};

	const currency = erpApiResult?.monthData[0]?.price.currency || '';

	return {
		allDataByCountry,
		byCountry,
		byMonth,
		currency,
		erpImpactedCountries,
		priceChangesByMonth,
		sortedDates,
		stats,
		cumulativeStats,
	};
};
